import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

type RefreshArticlesDialogParams = { mode: 'selected'; articleIds: string[] } | { mode: 'all' };

type Props = {
  isLoading: boolean;
  onConfirm: (params: RefreshArticlesDialogParams) => void;
};

function isRefreshArticlesDialogParams(params: unknown): params is RefreshArticlesDialogParams {
  if (!params || typeof params !== 'object') return false;
  const p = params as Record<string, unknown>;
  return p['mode'] === 'all' || (p['mode'] === 'selected' && Array.isArray(p['articleIds']));
}

export const RefreshArticlesDialog = ({ isLoading, onConfirm }: Props) => {
  const { t } = useTranslation('knowledge_base');
  const { isOpen, closeModal, getParams } = useModalStore();

  const open = isOpen(MODAL_NAMES.REFRESH_ARTICLES_DIALOG);
  const rawParams = open ? getParams() : null;
  const params = isRefreshArticlesDialogParams(rawParams) ? rawParams : null;

  const mode = params?.mode ?? 'all';
  const articleIds = mode === 'selected' ? params.articleIds : [];
  const count = articleIds.length;

  const title =
    mode === 'all'
      ? t('refresh_confirmation_modal.title_all')
      : t('refresh_confirmation_modal.title_selected', { count });

  const handleRequestClose = () => {
    if (isLoading) return;
    closeModal();
  };

  const handleConfirm = () => {
    if (isLoading) return;
    if (!params) return;
    onConfirm(params);
  };

  if (!open) {
    return null;
  }

  return (
    <AlertDialog open={open} onOpenChange={(value) => !value && handleRequestClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('refresh_confirmation_modal.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Separator />
        <AlertDialogFooter>
          <AlertDialogCancel asChild disabled={isLoading}>
            <Button variant="secondary" disabled={isLoading}>
              {t('button.cancel')}
            </Button>
          </AlertDialogCancel>
          <Button variant="dark" disabled={isLoading} onClick={handleConfirm}>
            {isLoading && <Spinner className="text-white h-4" />}
            {t('refresh_confirmation_modal.confirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
